import { useEffect, useRef } from "react";
import { Breed, Species } from "../../../types";
import { UseContactDetailsValidationReturn } from "./useContactDetailsValidation";
import { useSpeciesAndBreeds } from "./useSpeciesAndBreeds";

interface UseSpeciesBreedRestorationProps {
  savedSpecies?: Species;
  savedBreed?: Breed;
  speciesAndBreeds: ReturnType<typeof useSpeciesAndBreeds>;
  validation: UseContactDetailsValidationReturn;
  onRestorationComplete: () => void;
  onSpeciesRestored?: (species: Species) => void;
}

/**
 * Hook to handle restoration of saved species and breed selections.
 * Restores species/breed after the lists are loaded from the API.
 */
export function useSpeciesBreedRestoration({
  savedSpecies,
  savedBreed,
  speciesAndBreeds,
  validation,
  onRestorationComplete,
  onSpeciesRestored
}: UseSpeciesBreedRestorationProps) {
  const didSetSavedSpecies = useRef(false);
  const didSetSavedBreed = useRef(false);
  const restorationAttempted = useRef(false);

  // Restore species
  useEffect(() => {
    // If no species to restore, mark as complete immediately
    if (!savedSpecies && !restorationAttempted.current) {
      restorationAttempted.current = true;
      onRestorationComplete();
      return;
    }

    // Already restored successfully
    if (didSetSavedSpecies.current) {
      return;
    }

    // Nothing to restore
    if (!savedSpecies) {
      return;
    }

    // Wait for species list to be loaded
    if (
      speciesAndBreeds.species.length === 0 ||
      speciesAndBreeds.speciesItems.length === 0
    ) {
      return;
    }

    // Find the saved species in the loaded data
    const found = speciesAndBreeds.species.find((s: Species) => s.id === savedSpecies.id);
    const foundItem = speciesAndBreeds.speciesItems.find(
      (item: { value: string }) => item.value === savedSpecies.id.toString()
    );

    if (found && foundItem) {
      // Call onSpeciesSelected to update the selection
      // This will trigger breed list to be populated
      speciesAndBreeds.onSpeciesSelected(foundItem);

      // Mark species as valid since it was successfully loaded
      validation.setPatientValidation(prev => ({
        ...prev,
        petSpeciesValid: true
      }));

      didSetSavedSpecies.current = true;

      // Notify parent component that species was restored
      if (onSpeciesRestored) {
        onSpeciesRestored(found);
      }

      // Don't call onRestorationComplete yet if we have a breed to restore
      if (!savedBreed) {
        restorationAttempted.current = true;
        onRestorationComplete();
      }
    }
  }, [
    savedSpecies,
    savedBreed,
    speciesAndBreeds.species,
    speciesAndBreeds.speciesItems,
    speciesAndBreeds.onSpeciesSelected,
    validation,
    onRestorationComplete
  ]);

  // Restore breed (after species is selected and breed list populates)
  useEffect(() => {
    // Only attempt breed restoration if:
    // 1. Species has been restored
    // 2. We have a breed to restore
    // 3. We haven't already restored the breed
    if (!didSetSavedSpecies.current || !savedBreed || didSetSavedBreed.current) {
      return;
    }

    // Wait for breed list to be populated (happens after species selection)
    if (
      !speciesAndBreeds.onBreedSelected ||
      speciesAndBreeds.breeds.length === 0 ||
      speciesAndBreeds.breedItems.length === 0
    ) {
      return;
    }

    const foundBreed = speciesAndBreeds.breeds.find(
      (b: Breed) => b.code === savedBreed.code
    );
    const foundBreedItem = speciesAndBreeds.breedItems.find(
      (item: { value: string }) => item.value === savedBreed.code.toString()
    );

    if (foundBreed && foundBreedItem) {
      speciesAndBreeds.onBreedSelected(foundBreedItem);
      didSetSavedBreed.current = true;
    }

    // Breed restoration complete (whether successful or not)
    restorationAttempted.current = true;
    onRestorationComplete();
  }, [
    savedBreed,
    didSetSavedSpecies.current,
    speciesAndBreeds.breeds,
    speciesAndBreeds.breedItems,
    speciesAndBreeds.onBreedSelected,
    onRestorationComplete
  ]);

  // Additional effect to check if restoration completed via selectedSpecies
  useEffect(() => {
    if (
      savedSpecies &&
      !didSetSavedSpecies.current &&
      speciesAndBreeds.selectedSpecies?.id === savedSpecies.id
    ) {
      // Species was successfully restored, ensure validation is set
      validation.setPatientValidation(prev => ({
        ...prev,
        petSpeciesValid: true
      }));

      didSetSavedSpecies.current = true;

      // Notify parent component that species was restored
      if (onSpeciesRestored) {
        onSpeciesRestored(speciesAndBreeds.selectedSpecies);
      }

      // Only mark restoration as complete if there's no breed to restore
      if (!savedBreed) {
        restorationAttempted.current = true;
        onRestorationComplete();
      }
    }
  }, [
    savedSpecies,
    savedBreed,
    speciesAndBreeds.selectedSpecies,
    validation,
    onRestorationComplete,
    onSpeciesRestored
  ]);
}
